/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { getApiUrl } from "@/utils/baseUrl";

export interface OverviewProblem {
  type: "router" | "agent" | "switch" | "docker" | "service" | "packetloss" | "panel" | "cert" | "disk";
  name: string;
  reason: string;
  severity: "crit" | "warn";
}

export interface OverviewCategory {
  up: number;
  total: number;
}

export interface MutedProblem {
  type: string;
  name: string;
  reason?: string;
  /** unix seconds; 0 = forever */
  until: number;
  created: number;
}

export interface Overview {
  problems: OverviewProblem[];
  muted?: MutedProblem[];
  categories: {
    routers: OverviewCategory;
    agents: OverviewCategory;
    switches: OverviewCategory;
    docker: OverviewCategory;
    services: OverviewCategory;
    packetloss: OverviewCategory;
  };
}

export async function getOverview(): Promise<Overview> {
  const response = await fetch(getApiUrl("/overview"), { credentials: "include" });
  if (!response.ok) throw new Error("Failed to fetch overview");
  return response.json();
}

export async function muteProblem(p: { type: string; name: string; reason?: string; seconds: number }): Promise<void> {
  const response = await fetch(getApiUrl("/overview/mute"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(p),
  });
  if (!response.ok) throw new Error("Failed to mute");
}

export async function unmuteProblem(p: { type: string; name: string }): Promise<void> {
  const response = await fetch(getApiUrl("/overview/unmute"), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(p),
  });
  if (!response.ok) throw new Error("Failed to unmute");
}

export interface Incident {
  id: number;
  kind: string;
  name: string;
  reason: string;
  severity: "crit" | "warn";
  openedAt: string;
  resolvedAt: string | null;
}

export async function getIncidents(): Promise<Incident[]> {
  // Ask for the full server cap so the incident timeline has history to group;
  // the compact Event-log widget just slices the first few.
  const response = await fetch(getApiUrl("/incidents?limit=200"), { credentials: "include" });
  if (!response.ok) throw new Error("Failed to fetch incidents");
  const data = await response.json();
  return data.incidents ?? [];
}

export interface Anomaly {
  monitor: string;
  host: string;
  metric: "latency" | "loss";
  unit: string;
  current: number;
  baseline: number;
  score: number;
}

export async function getAnomalies(): Promise<Anomaly[]> {
  const response = await fetch(getApiUrl("/anomalies"), { credentials: "include" });
  if (!response.ok) throw new Error("Failed to fetch anomalies");
  const data = await response.json();
  return data.anomalies ?? [];
}

export interface ServiceUptime {
  name: string;
  current: "up" | "down" | "unknown";
  uptime24h: number;
  uptime7d: number;
  uptime30d: number;
}

export async function getServiceUptime(): Promise<ServiceUptime[]> {
  const response = await fetch(getApiUrl("/uptime"), { credentials: "include" });
  if (!response.ok) throw new Error("Failed to fetch uptime");
  const data = await response.json();
  return data.services ?? [];
}
